import React, { useState } from "react";
import { Store } from "lucide-react";

const FAVICON_PATH_PREFIX = "/api/v1/product-sourcing/sources/";
// Registry ids may include hyphens (e.g. hy-vee_com, us_schleich-s_com).
const SOURCE_ID_RE = /^[a-z0-9][a-z0-9_-]{0,120}$/;

function isReadySource(source) {
  if (source?.selectable === true) return true;
  return Boolean(source?.enabled && source?.operational);
}

/** Ezisell-hosted favicon URL for a source, or null. Only the cached Ezisell
 * endpoint is ever used — never a retailer origin or third-party service.
 * Ready rows may optimistically hit the cache path; Coming soon stays on the
 * neutral initial until ``favicon_url`` is present in the directory. */
export function faviconSrc(source) {
  const path = source?.favicon_url;
  if (typeof path === "string" && path.startsWith(FAVICON_PATH_PREFIX)) {
    return `${process.env.REACT_APP_BACKEND_URL || ""}${path}`;
  }
  if (!isReadySource(source)) return null;
  const sid = typeof source?.source_id === "string" ? source.source_id.trim() : "";
  if (SOURCE_ID_RE.test(sid)) {
    return `${process.env.REACT_APP_BACKEND_URL || ""}${FAVICON_PATH_PREFIX}${sid}/favicon`;
  }
  return null;
}

export function retailerInitial(source) {
  const text = String(source?.name || source?.domain || "?").trim();
  return (text[0] || "?").toUpperCase();
}

/**
 * @param {"sm"|"md"|"lg"} size
 * @param {"tile"|"bleed"} variant
 *   - tile: padded icon inside a bordered square (picker / chips)
 *   - bleed: artwork fills a rounded mask (Results summary)
 * @param {"cover"|"contain"} objectFit  bleed only; cover for square favicons
 */
export default function RetailerLogo({
  source,
  size = "md",
  variant = "tile",
  objectFit = "cover",
}) {
  const src = faviconSrc(source);
  const [failedSrc, setFailedSrc] = useState(null);
  const showImage = src && failedSrc !== src;
  const bleed = variant === "bleed";
  return (
    <span
      className={`as-retailer-logo ${size}${bleed ? " as-retailer-logo-bleed" : ""}`}
      aria-hidden="true"
      data-testid="retailer-logo"
      data-variant={variant}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          width={bleed ? 34 : 28}
          height={bleed ? 34 : 28}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          style={bleed ? { objectFit } : undefined}
          onError={() => setFailedSrc(src)}
          data-testid="retailer-logo-img"
        />
      ) : bleed ? (
        <span className="as-retailer-logo-fallback" data-testid="retailer-logo-fallback">
          <Store />
        </span>
      ) : (
        <span className="as-retailer-logo-initial" data-testid="retailer-logo-initial">
          {retailerInitial(source)}
        </span>
      )}
    </span>
  );
}
